"use client";

import Image from "next/image";
import React, { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { usePathname } from "next/navigation";
import parse from "html-react-parser";
import { FaArrowRight } from "react-icons/fa";

interface Faq {
  title: string;
  article: string;
  image?: string | null;
}

export default function FaqSection() {
  const pathname = usePathname();

  const [faqs, setFaqs] = useState<Faq[]>([]);
  const [faqsToShow, setFaqsToShow] = useState<Faq[]>([]);
  const [loading, setLoading] = useState(true);

  /* ===============================
     SLUG RESOLUTION
  =============================== */
  let rawSlug = pathname?.split("/").filter(Boolean).pop() || "home";

  if (pathname?.includes("/country-domains/")) {
    rawSlug = "country-domains";
  }

  /* ===============================
     BOOTSTRAP JS
  =============================== */
  useEffect(() => {
    import("bootstrap/dist/js/bootstrap.bundle.min.js");
  }, []);

  /* ===============================
     FETCH FAQS
  =============================== */
  useEffect(() => {
    const fetchFaqs = async () => {
      try {
        const res = await fetch("/api/faq", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ rawSlug }),
        });

        const data = await res.json();
        const articles = Array.isArray(data?.articles) ? data.articles : [];

        setFaqs(articles);
        setFaqsToShow(articles.slice(0, 6));
      } catch (err) {
        console.error("FAQ fetch failed", err);
      } finally {
        setLoading(false);
      }
    };

    fetchFaqs();
  }, [rawSlug]);

  const loadMore = () => {
    setFaqsToShow((prev) => [
      ...prev,
      ...faqs.slice(prev.length, prev.length + 6),
    ]);
  };

  /* ===============================
     RENDER
  =============================== */

  return (
    <section id="faq-section" className="container-lg my-5">
      <div className="text-center mb-4">
        <h2 className="fw-bold fs-2">Frequently Asked Questions</h2>
      </div>

      {/* 🔄 LOADING STATE */}
      {loading && (
        <p className="text-center text-muted">Loading FAQs…</p>
      )}

      {/* ❌ EMPTY STATE */}
      {!loading && faqs.length === 0 && (
        <div className="text-center text-muted mt-3">
          <p className="mb-1 fw-semibold">
            No FAQs available for this page yet.
          </p>
          <p className="small">
            If you have questions, feel free to contact our support team.
          </p>
        </div>
      )}

      {/* ✅ FAQ LIST */}
      {!loading && faqs.length > 0 && (
        <>
          <div className="accordion" id="faq-accordion">
            {faqsToShow.map((item, i) => (
              <div className="accordion-item my-3" key={i}>
                <h2 className="accordion-header">
                  <button
                    className="accordion-button collapsed fw-semibold"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target={`#faq-${rawSlug}-${i}`}
                  >
                    {parse(item.title)}
                  </button>
                </h2>

                <div
                  id={`faq-${rawSlug}-${i}`}
                  className="accordion-collapse collapse"
                  data-bs-parent="#faq-accordion"
                >
                  <div className="accordion-body">
                    {parse(item.article)}

                    {item.image && (
                      <div className="mt-3">
                        <Image
                          src={item.image}
                          alt="FAQ"
                          width={900}
                          height={500}
                          className="img-fluid"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {faqsToShow.length < faqs.length && (
            <div className="text-center mt-4">
              <Button className="orangeBtn" onClick={loadMore}>
                Load More <FaArrowRight />
              </Button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
